import { useQueries } from "@tanstack/react-query";
import type { Account, Category } from "gsintegrations";
import { mediumRepository } from "../repositories/mediumRepository";
import { terreiroRepository } from "../repositories/terreiroRepository";

/**
 * Query keys for categories and bank accounts
 */
export const categoriesAndAccountsQueryKeys = {
  categories: (
    terreiroId: string | undefined,
    mediumId: string | undefined,
    flow: string | undefined
  ) => {
    if (terreiroId) {
      return ["categories", flow, "terreiro", terreiroId] as const;
    }
    if (mediumId) {
      return ["categories", flow, "medium", mediumId] as const;
    }
    return ["categories", flow, "none"] as const;
  },
  bankAccounts: (
    terreiroId: string | undefined,
    mediumId: string | undefined
  ) => {
    if (terreiroId) {
      return ["bank-accounts", "terreiro", terreiroId] as const;
    }
    if (mediumId) {
      return ["bank-accounts", "medium", mediumId] as const;
    }
    return ["bank-accounts", "none"] as const;
  },
};

/**
 * Fetches categories (with dynamic flow query param) and bank accounts in parallel.
 * Supports both terreiro and medium contexts.
 * @param terreiroId The terreiro ID to fetch data for.
 * @param mediumId The medium ID to fetch data for (fallback if terreiroId is missing).
 * @param flow The categories flow query param (e.g., "income" | "outcome").
 */
export function useCategoriesAndAccountsQuery(
  terreiroId?: string,
  mediumId?: string,
  flow?: string
) {
  const useTerreiro = !!terreiroId;
  const useMedium = !terreiroId && !!mediumId;

  const queries = useQueries({
    queries: [
      {
        queryKey: categoriesAndAccountsQueryKeys.categories(
          terreiroId,
          mediumId,
          flow
        ),
        queryFn: () =>
          useTerreiro || useMedium
            ? terreiroRepository.getTerreiroCategories(terreiroId ?? "", flow)
            : Promise.resolve([] as Category[]),
        enabled: useTerreiro || useMedium,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: 2,
        refetchOnMount: "always" as const,
      },
      {
        queryKey: categoriesAndAccountsQueryKeys.bankAccounts(
          terreiroId,
          mediumId
        ),
        queryFn: () => {
          if (useTerreiro) {
            return terreiroRepository.getTerreiroBankAccounts(
              terreiroId as string
            );
          }
          if (useMedium) {
            return mediumRepository.getBankAccountsByMedium(mediumId as string);
          }
          return Promise.resolve([] as Account[]);
        },
        enabled: useTerreiro || useMedium,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: 2,
        refetchOnMount: "always" as const,
      },
    ],
  });

  const [categoriesQuery, bankAccountsQuery] = queries;

  return {
    categories: (categoriesQuery.data || []) as Category[],
    bankAccounts: (bankAccountsQuery.data || []) as Account[],
    isCategoriesLoading: categoriesQuery.isLoading,
    isBankAccountsLoading: bankAccountsQuery.isLoading,
    isAnyLoading: queries.some((q) => q.isLoading),
    categoriesError: categoriesQuery.error,
    bankAccountsError: bankAccountsQuery.error,
    hasAnyError: queries.some((q) => q.error),
    refetchAll: () => queries.forEach((q) => q.refetch()),
    refetchCategories: categoriesQuery.refetch,
    refetchBankAccounts: bankAccountsQuery.refetch,
    isSuccess: queries.every((q) => q.isSuccess),
    isError: queries.some((q) => q.isError),
  };
}
